class gTagEvents {
    constructor() {
        this.delegateBody = new Delegate(document.body);
        
        this.bindEvents();
        this.observeDOMChanges();
        this.handleIntersects();
        this.bindSubscriptionDropdownTracking();
        this.bindStickyAtcCapture();

        this.interceptCartApi();
    }

    bindEvents() {
        this.delegateBody.on("click", "button[name='checkout']", this.handleCheckout.bind(this));
        this.delegateBody.on("click", '[data-action="ga-send-select"]', this.handleSelectItem.bind(this));
        this.delegateBody.on("click", ".swiper-zoom-target", this.handleProductImageZoomTargetClick.bind(this));
        this.delegateBody.on('variant:changed:gtm', this.handleVariantChange.bind(this));
        this.delegateBody.on("click", 'button[data-action="show-more"]', this.handleShowMoreToggleClick.bind(this));
        this.delegateBody.on("click", ".Dropdown--ButtonPDP", this.handlePdpDropdownClick.bind(this));
        this.delegateBody.on("click", "[data-gtm-rec] .cardWrapper--Link", this.handleRecommendedProductClick.bind(this));
    }

    handleRecommendedProductClick(event) {
        const link = event.target.closest(".cardWrapper--Link");
        if (!link) return;

        const container = link.closest("[data-gtm-rec]");
        if (!container) return;

        const eventName = container.getAttribute("data-gtm-rec");
        if (!eventName) return;

        window.dataLayer = window.dataLayer || [];
        const payload = { event: eventName };
        window.dataLayer.push(payload);
        console.log(`dataLayer push -> ${eventName}:`, payload);
    }

    handlePdpDropdownClick(event) {
        const button = event.target.closest(".Dropdown--ButtonPDP");
        if (!button) return;

        const titleRaw = (button.getAttribute("data-title") || "").trim();
        if (!titleRaw) return;

        const title = titleRaw.toLowerCase();
        let eventName = null;

        if (title === "product care") eventName = "product_product-care-info";
        else if (title === "shipping") eventName = "product_shipping-info";
        else return;

        window.dataLayer = window.dataLayer || [];
        const payload = { event: eventName };
        window.dataLayer.push(payload);
        console.log(`dataLayer push -> ${eventName}:`, payload, { title: titleRaw });
    }

    bindStickyAtcCapture() {
        // Capture phase so we still see the click even if other code stops propagation.
        document.addEventListener("click", (event) => {
            const button = event.target.closest(".ProductActions.is-sticky ");
            if (!button) return;

            console.log("ATC click captured", button);

            window.dataLayer = window.dataLayer || [];
            const payload = { event: "product_sticky_add_to_cart" };
            window.dataLayer.push(payload);
            console.log("dataLayer push -> product_sticky_add_to_cart:", payload);
        }, true);
    }

    handleShowMoreToggleClick(event) {
        const button = event.target.closest('button[data-action="show-more"]');
        if (!button) return;

        const wrapper = button.closest(".main-product__show-more");
        if (!wrapper) return;

        // The theme toggles `aria-expanded` synchronously; however handler ordering can vary.
        // Read state on next frame so we always observe the post-toggle state.
        requestAnimationFrame(() => {
            const content = wrapper.querySelector(".main-product__show-more-content");
            const expanded = content?.getAttribute("aria-expanded") === "true";

            const eventName = expanded
                ? "product_description_show-more"
                : "product_description_show-less";

            window.dataLayer = window.dataLayer || [];
            const payload = { event: eventName };
            window.dataLayer.push(payload);
            console.log(`dataLayer push -> ${eventName}:`, payload);
        });
    }

    handleProductImageZoomTargetClick(event) {
        const target = event.target.closest(".swiper-zoom-target");
        if (!target) return;

        const width = window.innerWidth || document.documentElement.clientWidth || 0;
        const eventName = width > 1025 ? "product_image_zoom_desktop" : "product_image_click_mobile";

        window.dataLayer = window.dataLayer || [];
        const payload = { event: eventName };
        window.dataLayer.push(payload);

        console.log(`dataLayer push -> ${eventName}:`, payload);
    }

    bindSubscriptionDropdownTracking() {
        // Close/uncheck is dispatched from theme logic and bubbles.
        document.addEventListener("subscription:dropdown:close", () => {
            window.dataLayer = window.dataLayer || [];
            const payload = { event: "product_subscribe_uncheck-box" };
            window.dataLayer.push(payload);

            console.log("dataLayer push -> product_subscribe_uncheck-box:", payload);
        });

        // Keep a simple "check" on click (open) using current state.
        this.delegateBody.on(
            "click",
            '.subscription-dropdown__dropdown[data-action="toggle-sub-dropdown"]',
            (event) => {
                // Don't treat "learn more" clicks as subscribe toggles.
                if (event.target.closest('[data-action="open-sub-drawer"]')) return;

                const dropdown = event.target.closest('.subscription-dropdown__dropdown[data-action="toggle-sub-dropdown"]');
                if (!dropdown) return;
                if (dropdown.classList.contains("is-open")) return;

                window.dataLayer = window.dataLayer || [];
                const payload = { event: "product_subscribe_check-box" };
                window.dataLayer.push(payload);
                console.log("dataLayer push -> product_subscribe_check-box:", payload);
            }
        );

        // "Learn more" click inside subscribe module.
        this.delegateBody.on("click", '[data-action="open-sub-drawer"]', () => {
            window.dataLayer = window.dataLayer || [];
            const payload = { event: "product_subscribe_learn-more" };
            window.dataLayer.push(payload);
            console.log("dataLayer push -> product_subscribe_learn-more:", payload);
        });
    }

    observeDOMChanges() {
        this._pendingIntersectNodes = [];
        this._moScheduled = false;

        this._mutationObserver = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;
                    this._pendingIntersectNodes.push(node);
                }
            }

            if (this._moScheduled || !this._pendingIntersectNodes.length) return;
            this._moScheduled = true;
            requestAnimationFrame(() => {
                this._moScheduled = false;
                const roots = this._pendingIntersectNodes;
                this._pendingIntersectNodes = [];
                this.handleIntersects(roots);
            });
        });

        this._mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
        });
    }

    getIntersectionObserver() {
        if (this._intersectionObserver) return this._intersectionObserver;

        this._intersectionObserver = new IntersectionObserver(
            this.handleIntersection.bind(this),
            {
                root: null,
                rootMargin: "0px",
                threshold: 0.1,
            }
        );

        return this._intersectionObserver;
    }

    collectIntersectTargets(root) {
        const targets = [];
        if (!root || root.nodeType !== Node.ELEMENT_NODE) return targets;

        if (
            root.matches?.('[data-gtag-intersection][data-observed="false"]')
        ) {
            targets.push(root);
        }

        root
            .querySelectorAll?.('[data-gtag-intersection][data-observed="false"]')
            .forEach((el) => targets.push(el));

        return targets;
    }

    handleIntersects(roots) {
        const observer = this.getIntersectionObserver();
        const scanRoots = roots && roots.length ? roots : [document.body];
        const seen = new Set();

        scanRoots.forEach((root) => {
            this.collectIntersectTargets(root).forEach((item) => {
                if (seen.has(item) || item.dataset.observed === "true") return;
                seen.add(item);
                item.dataset.observed = "true";
                observer.observe(item);
            });
        });
    }

    handleIntersection(entries) {
        const observer = this.getIntersectionObserver();

        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            observer.unobserve(entry.target);

            let data = false;
            try {
                data = JSON.parse(
                    entry.target.querySelector('script[type="application/ld+json"]')
                        .textContent
                );
            } catch (e) {
                console.log("JSON parse failed", e);
            }

            if (!data) return;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push(data);
            console.log(data, "And pushed to dataLayer");
        });
    }

    // check if okay
    // how we fetch current variant can vary depending on the theme
    handleVariantChange(event) {        
        // get current variant id from url
        const params = new URLSearchParams(window.location.search);
        const variantIdParam = params.get("variant");
    
        if (!variantIdParam) return;
        const currentVariantID = Number(variantIdParam);
    
        // find current variant json in ShopifyAnalytics
        const variants = ShopifyAnalytics.meta.product.variants;
        let currentVariant = null;
    
        variants.forEach(variant => {        
            if (variant.id === currentVariantID) currentVariant = variant;
        })
    
        // get and update gtm script
        const dataElem = document.querySelector('.main-product__gtm-script');    
        if (!currentVariant || !dataElem) return;
        
        const data = JSON.parse(dataElem.textContent);
        const item = data.ecommerce.items[0];
        
        item.item_variant = currentVariant.public_title || "Default Title";
        item.item_id = currentVariant.id || "";
        item.item_sku = currentVariant.sku || "";
        item.price = currentVariant.price / 100;
        item.quantity = 1;
    
        data.ecommerce.value = currentVariant.price / 100;
    
        if (!data) return;
    
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(data);
        console.log(data, "And pushed to dataLayer");
    }

    interceptCartApi() {
        const originalFetch = window.fetch;
        const _this = this;

        window.fetch = async function (...args) {
            const input = args[0];
            const init = args[1];

            // fetch() can be called as fetch(url, init) OR fetch(Request) OR fetch(Request, init)
            const url =
                typeof input === "string"
                    ? input
                    : (input && typeof input.url === "string" ? input.url : "");

            // Don't interfere with non-cart requests (prevents noisy logs from third-party failures).
            if (!url || !url.includes("/cart/")) return originalFetch.apply(this, args);

            // Let fetch failures propagate naturally for cart requests too.
            const response = await originalFetch.apply(this, args);
            if (!response.ok) return response;

            // Trigger custom logic for cart clear/update based on request URL/body.
            if (url.includes(`/cart/clear`)) _this.cartClear();
            if (url.includes(`/cart/update`)) _this.cartUpdate(init?.body);

            // Only GTM-track events for relevant cart actions; never break fetch if parsing fails.
            try {
                window.dataLayer = window.dataLayer || [];

                if (url.includes(`/cart/add`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json();
                    _this.cartAdd(json, { body: init?.body });
                } else if (url.includes(`/cart/change`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json();
                    _this.cartChange(json);
                }
            } catch (_) {
                // Intentionally swallow tracking/parsing errors.
            }

            return response;
        };
    }

    // check if quantity is okay
    // when adding product that already exist in a cart fetch response thats returned does not have right product quantity
    // instead of showing how many products has been added it shows how many there are in cart
    async cartAdd(cart, request) {
        let cartItems = cart.items ? cart.items : [cart];
        cartItems = this.getDataFromRequest(cartItems, request);
        this.productAdded(cartItems);
    }

    async cartUpdate(body) {
        if (!body) return;
        const updates = JSON.parse(body).updates;
    
        const oldCart = await this.getCart();
        const cartItems = oldCart.items, itemsAdded = [], itemsRemoved = [];
    
        cartItems.forEach(item => {            
            const newQuantity = updates[item.key];
            if (typeof newQuantity === "number") {
                const change = newQuantity - item.quantity;
                item.quantity = Math.abs(change);
    
                change < 1 ? itemsRemoved.push(item) : itemsAdded.push(item);
            }
        })
    
        this.productAdded(itemsAdded);
        this.productsRemoved(itemsRemoved);
    }

    async cartChange(cart) {
        this.productAdded(cart.items_added);
        this.productsRemoved(cart.items_removed);
    }

    async cartClear() {
        const cart = await this.getCart();
        this.productsRemoved(cart.items);
    }

    async getCart() {
        try {
            const response = await fetch(window.routes.localeRootUrl + "/cart.js", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            return await response.json();

        } catch (error) {
            console.log('Fetch Error:', error);
        }
    }

    productAdded(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            'event': 'add_to_cart',
            'ecommerce': this.buildEcommerce(cartItems)
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> add_to_cart:", customEvent);
    }

    async productsRemoved(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
              'event': "custom_remove_from_cart",
              'ecommerce': this.buildEcommerce(cartItems)
        }
  
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> custom_remove_from_cart:", customEvent);
    }

    handleCheckout() {
        const button = event.target.closest("button[name='checkout']");
        if (!button) return;

        const script = button.parentElement.querySelector('[type="application/ld+json"]');
        const settings = JSON.parse(script.innerText);

        if (!settings) return;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(settings);

        console.log("dataLayer push -> checkoutFired:", settings);
    }

    handleSelectItem(event) {
        let target = event.target.closest('[data-action="ga-send-select"]');
        if (!target) return;

        const settings = JSON.parse(target.dataset.selectSettings);
        if (!settings) return;

        const customEvent = {
            event: "custom_select_item",
            ecommerce: {
                selected_item_list_id: settings.list_id,
                selected_item_list_name: settings.list_name,
                currency: ShopifyAnalytics.meta.currency,
                items: [settings]
            }
        }
    
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> custom_select_item:", customEvent);
    }

    buildEcommerce(cartItems) {
        const items = [];
        let totalPrice = 0;        

        cartItems.forEach((item) => {            
            const price = typeof item.price === "number" ? item.price / 100 : Number(item.price);
            totalPrice += price * item.quantity;
            
            const product = {
                'item_id': item.sku || "",
                'item_name': item.untranslated_product_title || item.title || "",
                'item_variant': item.untranslated_variant_title || "Default Title",
                'price': price, 
                'quantity': item.quantity,
                'category': item.properties?.["_gtm-category"] || "",
                'category2': item.properties?.["_gtm-category2"] || "",
                'item_brand': item.properties?.["_gtm-item_brand"] || "",
                'list_id': item.properties?.["_gtm-list_id"] || "",
                'list_name': item.properties?.["_gtm-list_name"] || ""
            }

            items.push(product);
        });

        return {
            'value': totalPrice,
            'currency': ShopifyAnalytics.meta.currency,
            'items': items
        }
    }

    getDataFromRequest(cartItems, request) {
        const body = request?.body || null;
        if (!body) return cartItems;

        let quantity = null;

        // Body can be FormData/URLSearchParams (has get), or a JSON string (theme uses JSON for /cart/add.js)
        if (typeof body === "string") {
            try {
                const parsed = JSON.parse(body);
                if (parsed && parsed.quantity != null) quantity = parsed.quantity;
            } catch (_) {
                // ignore
            }
        } else if (typeof body?.get === "function") {
            quantity = body.get("quantity");
        }

        if (quantity != null && cartItems?.[0]) cartItems[0].quantity = Number(quantity);

        return cartItems;
    }
}

new gTagEvents();